import React from 'react';
import { Brain, Image as ImageIcon, Loader2 } from 'lucide-react';
import { renderContent } from '../../utils/mathRenderer';
import { sanitizeClarificationMessageContent } from '../../utils/clarification';
import MessageDeliveryStatus from '../MessageDeliveryStatus';
import { ClarificationPayload } from '../../types/chat';

interface TextMessageProps {
  message: {
    content?: string | null;
    media_path?: string;
    status?: 'sending' | 'sent' | 'failed';
    created_at?: string | Date;
    clarification?: ClarificationPayload;
    pendingHint?: 'image_processing' | 'attachment_processing';
  };
  isUser: boolean;
  isProfileOpen: boolean;
  onSelectClarificationOption?: (optionId: string) => void;
  onEnableClarificationOther?: () => void;
}

// Correctly determine the root URL for static assets
const BACKEND_ROOT_URL = (() => {
  const apiUrlString = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  try {
    const url = new URL(apiUrlString);
    return `${url.protocol}//${url.host}`;
  } catch (e) {
    console.error("Error parsing REACT_APP_API_URL to determine backend root. Falling back to http://localhost:8000", e);
    return 'http://localhost:8000';
  }
})();

function getMediaUrl(mediaPathFromDb: string) {
  if (!mediaPathFromDb) return '';
  // If already an absolute URL, use as-is.
  if (mediaPathFromDb.startsWith('http://') || mediaPathFromDb.startsWith('https://')) {
    return mediaPathFromDb;
  }
  const cleanPath = mediaPathFromDb.startsWith('/') ? mediaPathFromDb.substring(1) : mediaPathFromDb;
  return `${BACKEND_ROOT_URL}/${cleanPath}`;
}

function inferSuggestedMediaKind(path: string): 'image' | 'audio' | 'video' | 'pdf' | 'file' {
  const p = (path || '').toLowerCase().split('?')[0].split('#')[0];
  const ext = p.includes('.') ? p.split('.').pop() || '' : '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'].includes(ext)) return 'audio';
  if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  return 'file';
}

export const TextMessage: React.FC<TextMessageProps> = ({
  message,
  isUser,
  isProfileOpen,
  onSelectClarificationOption,
  onEnableClarificationOther,
}) => {
  const [mediaLoadFailed, setMediaLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setMediaLoadFailed(false);
  }, [message.media_path]);

  const containerClass = isUser ? 'flex justify-end pr-2' : 'flex justify-start pl-2';
  const bubbleClass = `
    relative 
    w-fit 
    max-w-[80%]
    px-4 py-3 
    rounded-xl 
    ${isUser 
      ? 'bg-cyan-500/10 border border-cyan-400/20 text-[var(--text-primary)]' 
      : 'bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)]'
    }
    backdrop-blur-sm
    shadow-lg
    mb-2
    hover:border-cyan-400/30
    transition-all
  `;

  const isAssistantImageProcessingIndicator =
    !isUser &&
    message.status === 'sending' &&
    message.pendingHint === 'image_processing';
  const isAssistantAttachmentProcessingIndicator =
    !isUser &&
    message.status === 'sending' &&
    message.pendingHint === 'attachment_processing';
  const isAssistantTypingIndicator =
    !isUser &&
    message.status === 'sending' &&
    !(message.content || '').trim() &&
    !isAssistantImageProcessingIndicator &&
    !isAssistantAttachmentProcessingIndicator;
  const rawMessageContent = String(message.content || '');
  const displayMessageContent = isUser
    ? rawMessageContent
    : sanitizeClarificationMessageContent(rawMessageContent, message.clarification);
  const clarification = !isUser ? message.clarification : undefined;
  const clarificationOptions = clarification?.options ?? [];
  const hasClarificationOptions =
    !isUser &&
    clarificationOptions.length > 0;
  const clarificationCardTitle = String(
    clarification?.clarification_title || clarification?.question || ''
  ).trim();
  const clarificationCardSubtitle = String(
    clarification?.clarification_subtitle || ''
  ).trim();
  const clarificationOtherOptionLabel =
    String(clarification?.other_option_label || '').trim() || 'Other (type my own answer)';
  const shouldRenderTextContent = Boolean(displayMessageContent.trim());

  const renderTimestampAndStatus = () => {
    let displayTime = null;
    
    if (message.created_at) {
      const messageDate = new Date(message.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (messageDate.toDateString() === today.toDateString()) {
        displayTime = messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else if (messageDate.toDateString() === yesterday.toDateString()) {
        displayTime = `Yesterday, ${messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      } else {
        displayTime = messageDate.toLocaleDateString([], { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    }

    const statusConfig = {
      sending: { icon: '⏳', color: 'text-slate-400' },
      sent: { icon: '✓✓', color: 'text-cyan-400' },
      failed: { icon: '❌', color: 'text-red-400' }
    };

    const { icon, color } = statusConfig[message.status || 'sent'];

    return (
      <div className="flex items-center justify-end mt-2 gap-1.5">
        {displayTime && (
          <span className="text-xs text-[var(--text-secondary)]">{displayTime}</span>
        )}
        {message.status && isUser && (
          <MessageDeliveryStatus 
            status={message.status as 'sending' | 'sent' | 'delivered' | 'read'}
            timestamp={new Date(message.created_at || new Date())}
          />
        )}
        {!message.status && (
          <span className={`text-xs ${color}`}>{icon}</span>
        )}
      </div>
    );
  };

  return (
    <div className={containerClass}>
      <div className={`${bubbleClass} ${isProfileOpen ? 'z-50' : ''}`}>
        <div className="flex flex-col">
          {message.media_path &&
            (message.media_path.startsWith('static/') ||
              message.media_path.startsWith('/static/') ||
              message.media_path.startsWith('http://') ||
              message.media_path.startsWith('https://')) && (() => {
              const kind = inferSuggestedMediaKind(message.media_path);
              const url = getMediaUrl(message.media_path);
              if (kind === 'image') {
                if (mediaLoadFailed) {
                  return (
                    <span className="my-2 inline-flex items-center rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-200">
                      Media not available yet.
                    </span>
                  );
                }
                return (
                  <img
                    src={url}
                    alt="Suggested media"
                    className="rounded-lg max-w-[300px] border border-[var(--border-color)] hover:border-cyan-400/30 transition-colors my-2"
                    onError={() => setMediaLoadFailed(true)}
                  />
                );
              }
              if (kind === 'audio') {
                return (
                  <audio
                    controls
                    src={url}
                    className="my-2 w-[300px]"
                    aria-label="Suggested audio"
                  />
                );
              }
              if (kind === 'video') {
                return (
                  <video
                    controls
                    src={url}
                    className="my-2 max-w-[300px] rounded-lg border border-[var(--border-color)]"
                  />
                );
              }
              if (kind === 'pdf') {
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="my-2 text-sm text-cyan-400 hover:underline"
                  >
                    Open suggested PDF
                  </a>
                );
              }
              return null;
            })()}
          {isAssistantImageProcessingIndicator ? (
            <div className="flex items-center gap-3 py-2 px-1" role="status" aria-label="AI is processing your image">
              <span className="sr-only">AI is processing your image</span>
              <div className="relative flex items-center justify-center h-8 w-8 rounded-full bg-cyan-500/10 border border-cyan-400/30">
                <ImageIcon className="h-4 w-4 text-cyan-300" />
                <Loader2 className="h-3.5 w-3.5 text-cyan-200 animate-spin absolute -top-1 -right-1" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-cyan-100 font-medium">Analyzing your image</span>
                <span className="text-[11px] text-[var(--text-secondary)]">The AI is processing the image and your question...</span>
              </div>
            </div>
          ) : isAssistantAttachmentProcessingIndicator ? (
            <div className="flex items-center gap-3 py-2 px-1" role="status" aria-label="AI is processing your attachment">
              <span className="sr-only">AI is processing your attachment</span>
              <div className="relative flex items-center justify-center h-8 w-8 rounded-full bg-cyan-500/10 border border-cyan-400/30">
                <ImageIcon className="h-4 w-4 text-cyan-300" />
                <Loader2 className="h-3.5 w-3.5 text-cyan-200 animate-spin absolute -top-1 -right-1" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-cyan-100 font-medium">Processing your attachment</span>
                <span className="text-[11px] text-[var(--text-secondary)]">Reading and converting your file for the AI...</span>
              </div>
            </div>
          ) : isAssistantTypingIndicator ? (
            <div className="flex items-center gap-3 py-2 px-1" role="status" aria-label="AI is thinking">
              <span className="sr-only">AI is thinking</span>
              <div className="thinking-brain-container relative flex items-center justify-center">
                <div className="thinking-brain-glow absolute inset-0 rounded-full" />
                <Brain className="thinking-brain-icon h-5 w-5 text-[var(--accent-primary)]" />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-[var(--text-secondary)] font-medium thinking-text">Thinking</span>
                <span className="flex gap-0.5 items-end pb-[1px]">
                  <span className="thinking-dot thinking-dot-1 inline-block h-1 w-1 rounded-full bg-[var(--accent-primary)]" />
                  <span className="thinking-dot thinking-dot-2 inline-block h-1 w-1 rounded-full bg-[var(--accent-primary)]" />
                  <span className="thinking-dot thinking-dot-3 inline-block h-1 w-1 rounded-full bg-[var(--accent-primary)]" />
                </span>
              </div>
            </div>
          ) : (
            <>
              {shouldRenderTextContent && (
                <div
                  className="text-sm break-words whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: renderContent(displayMessageContent) }}
                />
              )}
              {hasClarificationOptions && (
                <div className="mt-3 pt-3 border-t border-[var(--border-color)]/60">
                  <div className="rounded-xl border border-cyan-400/35 bg-cyan-500/5 px-2.5 py-2.5">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-300 animate-pulse" />
                      <span className="text-[10px] uppercase tracking-wide text-cyan-200">Select one</span>
                    </div>
                    {(clarificationCardTitle || clarificationCardSubtitle) && (
                      <div className="mb-2.5 space-y-1">
                        {clarificationCardTitle && (
                          <p className="text-xs font-medium text-cyan-100">{clarificationCardTitle}</p>
                        )}
                        {clarificationCardSubtitle && (
                          <p className="text-[11px] leading-snug text-[var(--text-secondary)]">
                            {clarificationCardSubtitle}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      {clarificationOptions.slice(0, 3).map((opt, idx) => {
                        const optionId = String(opt.id || idx + 1);
                        const title = String(opt.title || `Option ${idx + 1}`);
                        const subtitle = opt.subtitle ? String(opt.subtitle) : '';
                        return (
                          <button
                            key={`${optionId}-${idx}`}
                            type="button"
                            className="w-full text-left px-3 py-2 rounded-lg text-xs border border-cyan-400/40 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20 hover:-translate-y-0.5 transition-all"
                            onClick={() => onSelectClarificationOption?.(optionId)}
                          >
                            <span className="font-medium">{title}</span>
                            {subtitle && (
                              <span className="block mt-0.5 text-[var(--text-secondary)] font-normal">{subtitle}</span>
                            )}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 rounded-lg text-xs border border-[var(--border-color)] bg-[var(--hover-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-cyan-400/40 hover:-translate-y-0.5 transition-all"
                        onClick={() => onEnableClarificationOther?.()}
                      >
                        {clarificationOtherOptionLabel}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {renderTimestampAndStatus()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 